import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { KanbanEdit, Pencil } from '@saleshandy/icons';
import { ImageIcon } from '@saleshandy/design-system';
import Modal from '../../../../shared/design-system/ui/modal/modal';
import {
  CategorizeByColumn,
  ColumnItem,
  DraggableInputModalProps,
  PaletteColor,
} from './type';
import { accessibleOnClick } from '../../../../shared/utils';
import {
  getViewModalHeaderTitle,
  PRESET_COLORS,
} from '../draggable-input/helper';
import { capitalize } from '../../../../shared/utils/string';
import SortableColumnField from '../draggable-input/draggable-input';
import { DotsVerticals } from '../../../../shared/svg';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import Input from '../../../../shared/design-system/components/input';
import { OverlayTooltip } from '../../../../shared/design-system/components/overlay';
import { Images } from '../../../../shared/app-constants';
import { Plus } from '../../../../shared/svg/plus';
import {
  getIsRequestPending,
  getIsRequestSucceeded,
} from '../../../../shared/utils/get-request-status';
import toaster, { Theme } from '../../../../shared/toaster';

const DUPLICATE_ERROR_MESSAGE = 'A column with this name already exists.';
const DEFAULT_FALLBACK_COLOR = PRESET_COLORS[0];

const DraggableInputModal: React.FC<DraggableInputModalProps> = ({
  show,
  onClose,
  initialColumns,
  onSave,
  enableVisibilityToggle,
  initialTitle,
  focusColumnValue,
  isDefault,
  updateKanbanViewRequestStatus,
  createCustomFieldRequestMessage,
  selectedCategoryName,
  setProspectViewIdFilter,
  isProspectEnrollmentEnabled,
}) => {
  const headerTitle = initialTitle || getViewModalHeaderTitle();
  const [columns, setColumns] = useState<ColumnItem[]>([]);
  const [activeColorPickerId, setActiveColorPickerId] = useState<string | null>(
    null,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [editedTitle, setEditedTitle] = useState<string>(headerTitle || '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveAttempted, setSaveAttempted] = useState<boolean>(false);
  const [viewNameError, setViewNameError] = useState<boolean>(false);
  const [newlyAddedColumnId, setNewlyAddedColumnId] = useState<string | null>(
    null,
  );
  const isDisabled = isDefault === 1;

  const noColumnValueAdded = columns?.every(
    (column) => column?.value?.trim() === '',
  );

  const maxCharacterExceeded = columns
    ?.map((column) => column?.value)
    ?.some((value) => value.trim().length > 28);

  const computedColorPalette = useMemo<PaletteColor[]>(
    () => [...PRESET_COLORS],
    [],
  );

  const addNewColumnDisabled = isDisabled || columns?.length >= 50;

  const addNewColumnDisabledTooltipText = () => {
    if (isDisabled) {
      return 'New columns can’t be added when the view is categorized by a system category.';
    }
    if (columns?.length >= 50) {
      return 'You can add only up to 50 columns in a particular view.';
    }
    return '';
  };

  const isLoading = getIsRequestPending(updateKanbanViewRequestStatus);

  const createColumnId = () =>
    `categorize-column-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const computedDefaultColor =
    computedColorPalette[0] ?? DEFAULT_FALLBACK_COLOR;

  const handleSave = () => {
    if (Object.keys(fieldErrors)?.length) {
      return;
    }
    setSaveAttempted(true);
    setIsSaving(true);
    setProspectViewIdFilter?.(null);
    onSave?.(
      columns
        ?.filter((column) => column?.value.trim())
        ?.map((column) => ({
          value: column?.value,
          color: column?.color,
          isHidden: column?.isHidden,
          borderColor: column?.borderColor,
        })),
      editedTitle,
    );
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDefaultColor = () => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        color: computedDefaultColor.fill,
        borderColor: computedDefaultColor.border,
      })),
    );
  };

  useEffect(() => {
    if (!columns.length) {
      handleDefaultColor();
    }
  }, []);

  const createColumn = useCallback(
    (
      value = '',
      paletteColor?: PaletteColor,
      isHidden = false,
      isNew = true,
    ): ColumnItem => {
      const resolvedPalette = paletteColor ?? computedDefaultColor;
      return {
        id: createColumnId(),
        value,
        color: resolvedPalette.fill,
        borderColor: resolvedPalette.border,
        isHidden,
        isNew,
      };
    },
    [computedDefaultColor],
  );

  const computeDuplicateErrors = useCallback(
    (items: ColumnItem[], targetId?: string) => {
      if (targetId) {
        const targetColumn = items?.find((column) => column?.id === targetId);

        if (targetColumn?.isHidden) {
          return {};
        }

        if (targetColumn) {
          const trimmedValue = targetColumn?.value?.trim()?.toLowerCase() ?? '';

          if (trimmedValue) {
            const hasDuplicate = items?.some(
              (column) =>
                !column.isHidden &&
                column?.id !== targetId &&
                column?.value?.trim()?.toLowerCase() === trimmedValue,
            );

            if (hasDuplicate) {
              return {
                [targetId]: DUPLICATE_ERROR_MESSAGE,
              };
            }
          }
        }

        return {};
      }

      const valueMap = new Map<string, string[]>();

      items
        .filter((column) => !column.isHidden)
        .forEach(({ id, value }) => {
          const trimmedValue = value?.trim()?.toLowerCase();

          if (!trimmedValue) {
            return;
          }

          const ids = valueMap.get(trimmedValue) ?? [];
          ids.push(id);
          valueMap.set(trimmedValue, ids);
        });

      const duplicates: Record<string, string> = {};

      valueMap.forEach((ids) => {
        if (ids.length > 1) {
          const latestId = ids[ids.length - 1];
          duplicates[latestId] = DUPLICATE_ERROR_MESSAGE;
        }
      });

      return duplicates;
    },
    [],
  );

  const handleAddColumn = () => {
    // Prevent adding columns if limit is reached or modal is disabled
    if (addNewColumnDisabled) {
      return;
    }
    setColumns((prev) => {
      const newColumn = createColumn('');
      const updatedColumns = [...prev, newColumn];
      setFieldErrors((existing) => {
        if (!Object.keys(existing).length) {
          return existing;
        }

        return computeDuplicateErrors(updatedColumns);
      });
      setNewlyAddedColumnId(newColumn.id);
      return updatedColumns;
    });
    setActiveColorPickerId(null);
  };

  const handleRemoveColumn = (id: string) => {
    setColumns((prev) => {
      const updatedColumns = prev?.filter((column) => column?.id !== id);
      setFieldErrors((currentErrors) => {
        if (!Object.keys(currentErrors).length) {
          return currentErrors;
        }

        return computeDuplicateErrors(updatedColumns);
      });
      return updatedColumns;
    });
    setActiveColorPickerId((current) => (current === id ? null : current));
  };

  const handleToggleVisibility = (id: string) => {
    setColumns((prev) => {
      // Check if category is "Prospect Status" and if this is the last visible column
      const isProspectStatus =
        headerTitle?.toLowerCase() === 'prospect status' ||
        editedTitle?.toLowerCase() === 'prospect status';
      const visibleColumns = prev.filter((col) => !col.isHidden);
      const targetColumn = prev.find((col) => col.id === id);

      // Prevent hiding the last visible column when category is "Prospect Status"
      if (
        isProspectStatus &&
        !targetColumn?.isHidden &&
        visibleColumns.length === 1
      ) {
        return prev; // Don't allow hiding the last visible column
      }

      const updatedColumns = prev.map((column) =>
        column.id === id
          ? {
              ...column,
              isHidden: !column.isHidden,
            }
          : column,
      );

      setFieldErrors((currentErrors) => {
        if (!Object.keys(currentErrors).length) {
          return currentErrors;
        }
        return computeDuplicateErrors(updatedColumns);
      });

      return updatedColumns;
    });
    setActiveColorPickerId((current) => (current === id ? null : current));
  };

  const handleColumnChange = (id: string, value: string) => {
    setColumns((prev) => {
      const updatedColumns = prev?.map((column) =>
        column?.id === id
          ? {
              ...column,
              value,
            }
          : column,
      );

      setFieldErrors(computeDuplicateErrors(updatedColumns, id));

      return updatedColumns;
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) return;

    setColumns((current) => {
      const from = current.findIndex(
        (column) => column.id === String(active.id),
      );
      const to = current.findIndex((column) => column.id === String(over.id));

      if (from === -1 || to === -1) {
        return current;
      }

      return arrayMove(current, from, to);
    });
  };

  const handleColorChange = (id: string, color: PaletteColor) => {
    setColumns((prev) => {
      const updatedColumns = prev.map((column) =>
        column.id === id
          ? {
              ...column,
              color: color.fill,
              borderColor: color.border,
            }
          : column,
      );
      setFieldErrors((existing) => {
        if (!Object.keys(existing).length) {
          return existing;
        }

        return computeDuplicateErrors(updatedColumns);
      });
      return updatedColumns;
    });
    setActiveColorPickerId(null);
  };

  const handleToggleColorPicker = (id: string) => {
    setActiveColorPickerId((current) => (current === id ? null : id));
  };

  useEffect(() => {
    setActiveColorPickerId(null);
  }, []);

  useEffect(() => {
    if (show) {
      if (initialColumns?.length) {
        const nextColumns = initialColumns.map((initial) => {
          if (typeof initial === 'string') {
            return createColumn(initial, undefined, false, false);
          }

          const column = initial as CategorizeByColumn;

          return createColumn(
            column.value,
            {
              fill: column.color ?? computedDefaultColor.fill,
              border: column.borderColor ?? computedDefaultColor.border,
            },
            column.isHidden ?? false,
            false,
          );
        });

        setColumns(nextColumns);
        setFieldErrors(computeDuplicateErrors(nextColumns));
      } else {
        const nextColumns = [createColumn()];
        setColumns(nextColumns);
        setFieldErrors({});
      }
    }
  }, [show, initialColumns, createColumn, computeDuplicateErrors]);

  useEffect(() => {
    if (!show) {
      setActiveColorPickerId(null);
      setFieldErrors({});
      setSaveAttempted(false);
      setIsSaving(false);
      setNewlyAddedColumnId(null);
    }
  }, [show]);

  // Clear the focus flag after the newly added column has been rendered and focused
  useEffect(() => {
    if (newlyAddedColumnId) {
      // Use setTimeout to ensure the focus has been applied before clearing
      const timer = setTimeout(() => {
        setNewlyAddedColumnId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [newlyAddedColumnId]);

  useEffect(() => {
    if (saveAttempted && getIsRequestSucceeded(updateKanbanViewRequestStatus)) {
      setIsSaving(false);
      toaster.success(createCustomFieldRequestMessage, { theme: Theme.New });
    }
  }, [saveAttempted, updateKanbanViewRequestStatus]);

  useEffect(() => {
    if (saveAttempted && !isSaving) {
      onClose();
    }
  }, [saveAttempted, isSaving, onClose]);

  useEffect(() => {
    if (!activeColorPickerId) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        target.closest('.categorize-by-modal__color-picker') ||
        target.closest('.categorize-by-modal__color-swatch--trigger')
      ) {
        return;
      }

      setActiveColorPickerId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeColorPickerId]);

  const onSaveViewName = () => {
    if (viewNameError) return;
    setIsEditingTitle(false);
    if (editedTitle.trim() === '') {
      setEditedTitle(headerTitle);
    }
  };

  return (
    <Modal
      show={show}
      title={
        <div className="d-flex flex-column">
          <div
            className="categorize-by-modal-header-container"
            onDoubleClick={() => setIsEditingTitle(true)}
          >
            <KanbanEdit width={24} height={24} />
            {isEditingTitle ? (
              <Input
                type="text"
                value={
                  isDisabled && editedTitle
                    ? capitalize(editedTitle)
                    : editedTitle
                }
                onChange={(value: string) => {
                  setEditedTitle(value);
                  if (value.length > 100) {
                    setViewNameError(true);
                  } else {
                    setViewNameError(false);
                  }
                }}
                onBlur={() => {
                  onSaveViewName();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSaveViewName();
                  }
                }}
                // className="categorize-by-modal__title-input"
                className={
                  viewNameError
                    ? 'input-error categorize-by-modal__title-input'
                    : 'categorize-by-modal__title-input'
                }
                autoFocus
                disabled={isDisabled}
              />
            ) : (
              <TruncatedTextWithAutoTooltip
                text={
                  isDisabled && editedTitle
                    ? capitalize(editedTitle)
                    : editedTitle
                }
                className="categorize-by-modal-truncatedText"
              />
            )}
            {!isEditingTitle && !isDisabled && (
              <OverlayTooltip text="Edit category name">
                <div
                  className="pencil-icon-container"
                  {...accessibleOnClick(() => {
                    setIsEditingTitle(true);
                  })}
                >
                  <Pencil width={16} height={16} />
                </div>
              </OverlayTooltip>
            )}
          </div>
          {viewNameError && (
            <div className="error-text">
              Category name should be less than or equal to 100 characters
            </div>
          )}
        </div>
      }
      onHide={handleCancel}
      secondaryBtnClickedHandler={handleCancel}
      primaryBtnClickedHandler={handleSave}
      primaryBtnLabel={isLoading ? 'Saving...' : 'Save'}
      primaryBtnDisabled={
        Object.keys(fieldErrors)?.length > 0 ||
        isLoading ||
        viewNameError ||
        noColumnValueAdded ||
        maxCharacterExceeded
      }
      className="categorize-by-modal"
    >
      <div className="categorize-by-modal__content">
        <div className="categorize-by-modal__list">
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={columns
                .filter(
                  (column) =>
                    isProspectEnrollmentEnabled || column.value !== 'upcoming',
                )
                .map((column) => column.id)}
              strategy={verticalListSortingStrategy}
            >
              {columns
                .filter(
                  (column) =>
                    isProspectEnrollmentEnabled || column.value !== 'upcoming',
                )
                .map((column, index) => {
                  // Check if category is "Prospect Status"
                  const isProspectStatus =
                    headerTitle?.toLowerCase() === 'prospect status' ||
                    editedTitle?.toLowerCase() === 'prospect status';

                  // Count visible columns (after filtering)
                  const filteredColumns = columns.filter(
                    (col) =>
                      isProspectEnrollmentEnabled || col.value !== 'upcoming',
                  );
                  const visibleColumns = filteredColumns.filter(
                    (col) => !col.isHidden,
                  );

                  // Disable visibility toggle if this is the last visible column and category is "Prospect Status"
                  const disableVisibilityToggleForColumn =
                    isProspectStatus &&
                    !column.isHidden &&
                    visibleColumns.length === 1;

                  return (
                    <SortableColumnField
                      key={column.id}
                      column={column}
                      onChange={handleColumnChange}
                      onRemove={handleRemoveColumn}
                      shouldFocusOnMount={
                        (focusColumnValue != null &&
                          column.value?.trim()?.toLowerCase() ===
                            focusColumnValue?.trim()?.toLowerCase()) ||
                        newlyAddedColumnId === column.id
                      }
                      disableRemove={
                        !enableVisibilityToggle &&
                        filteredColumns.length === 1 &&
                        index === 0
                      }
                      isDuplicate={Boolean(fieldErrors[column.id])}
                      isColorSelectionEnabled={!column.isHidden}
                      onToggleColorPicker={handleToggleColorPicker}
                      onColorChange={handleColorChange}
                      isColorPickerOpen={activeColorPickerId === column.id}
                      availableColors={computedColorPalette}
                      errorMessage={fieldErrors[column.id]}
                      isHidden={Boolean(column.isHidden)}
                      isVisibilityToggleEnabled={enableVisibilityToggle}
                      onToggleVisibility={
                        enableVisibilityToggle
                          ? handleToggleVisibility
                          : undefined
                      }
                      disableVisibilityToggle={disableVisibilityToggleForColumn}
                      isDragDisabled={Boolean(column.isHidden)}
                      isNew={Boolean(column.isNew)}
                      isDisabled={isDisabled}
                      handleAddColumn={handleAddColumn}
                      selectedCategoryName={selectedCategoryName}
                    />
                  );
                })}
            </SortableContext>
          </DndContext>
        </div>

        <OverlayTooltip
          text={addNewColumnDisabledTooltipText()}
          className={`${addNewColumnDisabled ? 'd-flex' : 'd-none'}`}
        >
          <button
            type="button"
            className={`categorize-by-modal__add-row ${
              addNewColumnDisabled ? 'disabled cursor-not-allowed' : ''
            }`}
            disabled={addNewColumnDisabled}
          >
            <span className="categorize-by-modal__drag-handle categorize-by-modal__drag-handle--disabled">
              <DotsVerticals />
            </span>
            <span
              className="categorize-by-modal__color-swatch categorize-by-modal__color-swatch--disabled"
              style={{
                backgroundColor: computedDefaultColor?.fill,
                borderColor: computedDefaultColor?.border,
              }}
            />
            <span
              className="categorize-by-modal__add-label"
              {...(addNewColumnDisabled
                ? {}
                : accessibleOnClick(handleAddColumn))}
            >
              <Plus />
              Add New Column
            </span>
          </button>
        </OverlayTooltip>
      </div>
    </Modal>
  );
};

export default DraggableInputModal;
